/**
 * <biosphere-combo-buy> — the combo page's buy block and its sticky bar.
 *
 * Baseline without JS: one real /cart/add form per product. With JS the forms are
 * intercepted into a single /cart/add.js call. Cart state is published, never reached
 * for — biosphere-product-form.js does the same, and the drawer opens itself.
 *
 * Arithmetic is integer cents formatted through the shop's money format; ladder steps
 * arrive from theme settings, so no rate is known here.
 */
const ERROR_DISPLAY_MS = 10000;

class BiosphereComboBuy extends HTMLElement {
  #abort;
  #steps = [];
  #forms = [];
  #submit;
  #stickySubmit;
  #sticky;
  #error;
  #live;
  #messageTimer;
  #busy = false;

  connectedCallback() {
    this.#forms = Array.from(
      this.querySelectorAll(".biosphere-combo-buy__line"),
    );
    this.#submit = this.querySelector(".biosphere-combo-buy__submit");
    this.#error = this.querySelector(".biosphere-combo-buy__error");
    this.#live = this.querySelector(".biosphere-combo-buy__live");
    this.#sticky = this.querySelector(".biosphere-combo-buy__sticky");
    this.#stickySubmit = this.querySelector(
      ".biosphere-combo-buy__sticky-submit",
    );

    // One form cannot add two line items, which is the whole point of the swap.
    if (this.#forms.length < 2 || !this.#submit) return;

    const config = this.querySelector(".biosphere-combo-buy__config");
    try {
      this.#steps = JSON.parse(config?.textContent ?? "[]");
    } catch {
      this.#steps = [];
    }

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    for (const form of this.#forms) {
      form.addEventListener("submit", (event) => this.#onSubmit(event), {
        signal,
      });
      form.addEventListener("change", () => this.#render(), { signal });
      form.addEventListener("biosphere:quantity:change", () => this.#render(), {
        signal,
      });
    }

    this.#submit.addEventListener("click", (event) => this.#onSubmit(event), {
      signal,
    });
    this.#stickySubmit?.addEventListener(
      "click",
      (event) => this.#onSubmit(event),
      { signal },
    );

    // The combined button is the JS-only affordance; the per-line buttons are
    // the baseline it replaces. Swapped here so the no-JS page keeps a way to buy.
    this.#submit.hidden = false;
    for (const button of this.querySelectorAll(
      ".biosphere-combo-buy__line-submit",
    )) {
      button.hidden = true;
    }

    // JS-only affordances, rendered hidden and revealed once they can be acted on.
    if (this.#sticky) this.#sticky.hidden = false;

    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#messageTimer);
  }

  /** The chosen variant input for one line: the checked radio, or the lone hidden input. */
  #chosen(form) {
    return (
      form.querySelector('input[name="id"]:checked') ??
      form.querySelector('input[name="id"]')
    );
  }

  #quantityOf(form) {
    const input = form.querySelector(".biosphere-quantity__input");
    const value = Number.parseInt(input?.value ?? "", 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  #unitPriceOf(form) {
    const price = Number.parseInt(
      this.#chosen(form)?.dataset.unitPrice ?? "",
      10,
    );
    return Number.isFinite(price) ? price : 0;
  }

  /** Current basket state, in cents. */
  #state() {
    let units = 0;
    let gross = 0;
    const lines = [];
    for (const form of this.#forms) {
      const quantity = this.#quantityOf(form);
      const unitPrice = this.#unitPriceOf(form);
      units += quantity;
      gross += unitPrice * quantity;
      lines.push({ unitPrice, quantity });
    }
    const percent = this.#percentFor(units);

    let discount = 0;
    for (const line of lines) {
      discount += Math.floor((line.unitPrice * percent) / 100) * line.quantity;
    }
    return { units, gross, percent, discount, total: gross - discount };
  }

  /** The best rate the ladder gives this many units. Steps come from settings. */
  #percentFor(units) {
    let percent = 0;
    for (const step of this.#steps) {
      if (units >= step.quantity && step.percent > percent) {
        percent = step.percent;
      }
    }
    return percent;
  }

  /** The step above the one currently earned, or null at the top of the ladder. */
  #nextStep(units) {
    let next = null;
    for (const step of this.#steps) {
      if (step.quantity > units && (!next || step.quantity < next.quantity)) {
        next = step;
      }
    }
    return next;
  }

  #render() {
    const state = this.#state();

    for (const form of this.#forms) {
      const price = form.querySelector(".biosphere-combo-buy__line-price");
      if (price) {
        price.textContent = this.#money(
          this.#unitPriceOf(form) * this.#quantityOf(form),
        );
      }
      // Follows the chosen variant, so line and radio cannot disagree.
      const flavour = form.querySelector(".biosphere-combo-buy__line-flavour");
      if (flavour) {
        flavour.textContent = this.#chosen(form)?.dataset.flavour ?? "";
      }
    }

    const itemsTemplate =
      state.units === 1
        ? (this.dataset.itemsOneTemplate ?? "")
        : (this.dataset.itemsOtherTemplate ?? "");
    this.#write("items", itemsTemplate.replace("[count]", String(state.units)));
    this.#write("gross", this.#money(state.gross));
    this.#write("gross-strike", this.#money(state.gross));
    this.#write("total", this.#money(state.total));

    this.#write(
      "tier-label",
      state.percent > 0
        ? (this.dataset.tierTemplate ?? "").replace(
            "[percent]",
            String(state.percent),
          )
        : (this.dataset.tierNone ?? ""),
    );
    this.#write(
      "discount",
      state.discount > 0 ? "−" + this.#money(state.discount) : this.#money(0),
    );

    const stickyTemplate =
      state.discount > 0
        ? (this.dataset.stickySaveTemplate ?? "")
        : (this.dataset.stickyPlainTemplate ?? "");
    this.#write(
      "sticky-price",
      stickyTemplate
        .replace("[total]", this.#money(state.total))
        .replace("[saving]", this.#money(state.discount)),
    );

    this.#renderNudge(state);
    this.#publish(state);
  }

  #renderNudge(state) {
    const next = this.#nextStep(state.units);
    const template = this.dataset.nudgeTemplate ?? "";
    const short = next ? Math.max(1, next.quantity - state.units) : 0;
    const message =
      next && template
        ? template
            .replace("[count]", String(short))
            .replace("[percent]", String(next.percent))
        : "";
    for (const node of this.querySelectorAll(".biosphere-combo-buy__nudge")) {
      node.textContent = message;
      node.hidden = message === "";
    }
  }

  /** Line-item properties an app has put in this product's form. */
  #propertiesOf(form) {
    const properties = {};
    for (const field of form.querySelectorAll('[name^="properties["]')) {
      if (field.disabled || !field.value) continue;
      if (
        (field.type === "checkbox" || field.type === "radio") &&
        !field.checked
      ) {
        continue;
      }
      properties[field.name.slice("properties[".length, -1)] = field.value;
    }
    return properties;
  }

  /** The resolved line items: chosen variant and committed quantity per product. */
  #items() {
    const items = [];
    for (const form of this.#forms) {
      const id = this.#chosen(form)?.value;
      if (!id) continue;

      const item = { id: id, quantity: this.#quantityOf(form) };
      const properties = this.#propertiesOf(form);
      if (Object.keys(properties).length > 0) item.properties = properties;
      items.push(item);
    }
    return items;
  }

  /** Report the new state. The step-up section listens; nothing is reached into. */
  #publish(state) {
    this.dispatchEvent(
      new CustomEvent("biosphere:combo:change", {
        bubbles: true,
        detail: {
          items: this.#items(),
          units: state.units,
          gross: state.gross,
          percent: state.percent,
          discount: state.discount,
          total: state.total,
        },
      }),
    );
  }

  #write(part, value) {
    for (const node of this.querySelectorAll(".biosphere-combo-buy__" + part)) {
      node.textContent = value;
    }
  }

  /** Format cents through the shop's money format. */
  #money(cents) {
    const format = this.dataset.moneyFormat || "{{amount}}";
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (whole, name) => {
      switch (name) {
        case "amount_no_decimals":
          return this.#digits(cents, false, ",", ".");
        case "amount_with_comma_separator":
          return this.#digits(cents, true, ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return this.#digits(cents, false, ".", ",");
        case "amount_with_space_separator":
          return this.#digits(cents, true, " ", ",");
        case "amount_no_decimals_with_space_separator":
          return this.#digits(cents, false, " ", ",");
        case "amount_with_apostrophe_separator":
          return this.#digits(cents, true, "'", ".");
        default:
          return this.#digits(cents, true, ",", ".");
      }
    });
  }

  #digits(cents, withDecimals, thousands, point) {
    const negative = cents < 0;
    const absolute = Math.abs(Math.round(cents));
    const units = withDecimals
      ? Math.floor(absolute / 100)
      : Math.round(absolute / 100);
    const whole = String(units).replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const fraction = String(absolute % 100).padStart(2, "0");
    return (
      (negative ? "-" : "") + whole + (withDecimals ? point + fraction : "")
    );
  }

  async #onSubmit(event) {
    event.preventDefault();
    if (this.#busy) return;

    const items = this.#items();
    // Nothing may be POSTed without every line resolving to a variant.
    if (items.length !== this.#forms.length) return;

    this.#busy = true;
    this.#setBusy(true);

    // Published before the request, so the drawer opens on the click.
    document.dispatchEvent(
      new CustomEvent("biosphere:cart:adding", {
        detail: { source: this.dataset.comboId },
      }),
    );

    let payload;
    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });
      payload = await response.json();
    } catch {
      this.#fail(this.dataset.errorNetwork ?? "");
      // Republished whatever happened: the drawer opened on cart:adding and only
      // cart:change releases it.
      await this.#refreshCart();
      return;
    }

    if (payload && payload.status) {
      this.#fail(payload.message ?? this.dataset.errorNetwork ?? "");
      await this.#refreshCart();
      return;
    }

    this.#hideError();
    this.#announce(this.dataset.addedMessage ?? "");
    await this.#refreshCart("add");
    this.#busy = false;
    this.#setBusy(false);
  }

  async #refreshCart(action = "update") {
    let cart = null;
    try {
      const response = await fetch(this.dataset.cartUrl + ".json", {
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      cart = await response.json();
    } catch {
      // Published anyway, with no cart: returning quietly left the drawer veiled.
      // Every subscriber guards a missing cart.
    }
    document.dispatchEvent(
      new CustomEvent("biosphere:cart:change", {
        detail: { cart, action, source: this.dataset.comboId },
      }),
    );
    return cart;
  }

  #setBusy(busy) {
    this.#submit.disabled = busy;
    if (this.#stickySubmit) this.#stickySubmit.disabled = busy;
  }

  #fail(message) {
    this.#busy = false;
    this.#setBusy(false);
    if (this.#error) {
      this.#error.textContent = message;
      this.#error.hidden = false;
    }
    this.#announce(message);
  }

  #hideError() {
    if (this.#error) this.#error.hidden = true;
  }

  #announce(message) {
    if (!this.#live) return;
    clearTimeout(this.#messageTimer);
    this.#live.textContent = message;
    this.#messageTimer = setTimeout(() => {
      this.#live.textContent = "";
      this.#hideError();
    }, ERROR_DISPLAY_MS);
  }
}

if (!customElements.get("biosphere-combo-buy")) {
  customElements.define("biosphere-combo-buy", BiosphereComboBuy);
}
